/**
 * One runner for every write on a page.
 *
 * ⚠️ THIS IS THE ONLY PLACE A PAGE LEARNS THAT A REQUEST IS IN FLIGHT, and that
 * is the point. Nine pages each grew their own answer to the same three
 * questions — is something running, which control started it, and what do we
 * say when it lands — and no two of them answered alike: `busy`, `posting`,
 * `status === 'saving'`, `briefState === 'sending'`, `busyCharacterId`. Half of
 * them then spent the answer on rewriting a button's label, which is the one
 * place a progress report must never go: the label is what the control DOES,
 * and a control that renames itself mid-press has stopped saying so.
 *
 * What a caller gets instead:
 *
 *   run(key, fn, messages)  does the work, reports the outcome, and is the
 *                           only thing that knows how
 *   isPending(key)          drives that control's spinner
 *   isBusy                  drives everything else's disabled state
 *
 * ⚠️ ONE WRITE AT A TIME, ACROSS THE WHOLE RUNNER. Several pages already had
 * this rule and wrote it out by hand (`if (busy) return`), for a good reason
 * that generalises: the loot writes each answer with the WHOLE queue, so two in
 * flight together leave whichever replied last on screen — which need not be
 * the one that wrote last. It is enforced on a REF rather than on the state,
 * because two presses in one tick both read the same stale `false`.
 *
 * ⚠️ AND THE KEY IS NOT THE GUARD. `isPending` answers "is THIS control
 * working" so a row's own button can spin without the other nineteen spinning
 * with it; `isBusy` is what stops the twentieth being pressed. Making the guard
 * per-key would let two rows race, which is the bug this replaces.
 */

import { useCallback, useRef, useState } from 'react';
import { errorText } from './errors.ts';
import { notify } from './notify.tsx';

export interface ActionMessages {
  /**
   * What to say when the failure carries nothing usable of its own.
   *
   * ⚠️ A FALLBACK, NOT THE MESSAGE. The server's sentence wins — see
   * `errorText`. Write this as the thing that failed («Не удалось перенести
   * событие.»), because that is the half a network error cannot supply.
   */
  error: string;
  /**
   * What to say when it worked.
   *
   * Omitted when the action reports its own outcome — the loot writes and the
   * two bot triggers all have something more specific to say than «готово», and
   * a generic success toast stacked under theirs is one message too many.
   */
  success?: string;
}

export interface ActionRunner {
  /** The key of the action in flight, or null. */
  pending: string | null;
  /** Whether ANY action is in flight. This is what disables the other controls. */
  isBusy: boolean;
  /** Whether THIS control is the one working. This is what spins its spinner. */
  isPending: (key: string) => boolean;
  /**
   * Run one write.
   *
   * Resolves `true` when it worked — for the callers that close a panel, clear
   * a form or arm a next step only on success — and `false` both when it threw
   * and when it was refused for overlapping another action.
   */
  run: (key: string, fn: () => Promise<unknown>, messages: ActionMessages) => Promise<boolean>;
}

export function useAction(): ActionRunner {
  const [pending, setPending] = useState<string | null>(null);
  /*
    The guard, and it is deliberately not `pending`.

    Two presses inside one tick — a double tap, or a keyboard repeat — both run
    before React has re-rendered, so both would read `pending === null` and both
    would dispatch. The ref is written synchronously and is therefore true for
    the second one.
  */
  const inFlight = useRef(false);

  const run = useCallback(
    async (key: string, fn: () => Promise<unknown>, messages: ActionMessages) => {
      if (inFlight.current) return false;
      inFlight.current = true;
      setPending(key);

      try {
        await fn();
        if (messages.success !== undefined) notify.success(messages.success);
        return true;
      } catch (caught) {
        notify.error(errorText(caught, messages.error));
        return false;
      } finally {
        /*
          Cleared even if the component has since unmounted. `setPending` on an
          unmounted component is a no-op in React 18+, and the ref has to be
          released whatever happens — a runner stuck on `true` is a page whose
          every button has silently stopped working.
        */
        inFlight.current = false;
        setPending(null);
      }
    },
    [],
  );

  return {
    pending,
    isBusy: pending !== null,
    isPending: (key: string) => pending === key,
    run,
  };
}
